import { Input, InputNumber, Select } from '@aigc/antd';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { tryURL } from '@aigc/toolkit';
import {
  AudioOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  VideoCameraOutlined,
  VideoCameraTwoTone,
} from '@ant-design/icons';
import { useLens } from '@hookform/lenses';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Form,
  Popconfirm,
  Segmented,
  Tooltip,
  Upload,
} from 'antd';
import { RESET } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Watch, type DefaultValues } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import type { InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import { UploadedImageListModal } from '../../../components/uploaded-image-list-modal';
import { VirtualAvatarSelect } from '../../../features/assets/components/virtual-avatar-select';
import { LanguageDropdownControl } from '../../../features/common/components/language-dropdown-control';
import { ProductNameTip } from '../../../features/common/components/product-name-tip';
import { Permission } from '../../../features/common/permision';
import { ComplianceDrawer } from '../../../features/compliance/compliance-drawer';
import {
  createBeforeUpload,
  createUploadValidator,
  FileValidators,
  referenceVideoValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import type { FileDto } from '../../../features/file/model';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../../features/file/utils';
import { useAuthority } from '../../../features/me/hook';
import {
  DEFAULT_REFERENCE_IMAGE_MODEL,
  REFERENCE_IMAGE_MODEL_MAP,
} from '../../../features/reference-image/constant';
import { schema } from '../../../features/reference-image/schema';
import {
  useReadReferenceImagePageData,
  useSetReferenceImagePageData,
} from '../../../features/reference-image/store';
import { saveScript } from '../../../features/script/api';
import { ScriptType } from '../../../features/script/model';
import type { ScriptDto } from '../../../features/script/script.dto';
import {
  TaskType,
  type VideoGenerationTaskDto,
} from '../../../features/task/model';
import { DurationAlert } from '../../../features/video-model/components/duration-alert';
import { DurationTooltip } from '../../../features/video-model/components/duration-tooltip';
import { MultiPromptControl } from '../../../features/video-model/components/multi-prompt-control';
import { PromptControl } from '../../../features/video-model/components/prompt-control';
import {
  GenerationType,
  isSupportedAssets,
  videoModelSpecs,
  type VideoModelCode,
} from '../../../features/video-model/model-spec';
import {
  getGenerateVideoTaskMutationOptions,
  translatePromptMutationOptions,
} from '../../../features/video/api';
import { AspectRatioSelect } from '../../../features/video/components/aspect-ratio-select';
import { VideoGenerationTaskTable } from '../../../features/video/components/task-table';
import { useTryParsePrompt } from '../../../features/video/hook';
import type { GenerateVideoInputDto } from '../../../features/video/model';
import { ParseError, parsePrompt } from '../../../features/video/parse';
import {
  buildMultiPrompt,
  scriptTemplate,
} from '../../../features/video/utils';

type FormValues = InferType<typeof schema>;

export const VideoCreateMultiImage = (props: {
  isLongVideoSupported?: boolean;
}) => {
  const { message, modal, notification } = App.useApp();
  const onPreview = usePreviewFn();
  const [openOverlay] = useOverlay<FileDto>();
  const [openDrawer] = useOverlay<string>();
  const readPageData = useReadReferenceImagePageData(
    props.isLongVideoSupported ?? false,
  );
  const setReferenceImagePageData = useSetReferenceImagePageData(
    props.isLongVideoSupported ?? false,
  );
  const translate = useMutation(translatePromptMutationOptions);
  const pageData = readPageData();
  const [simpleMode, setSimpleMode] = useState(pageData.simple_mode ?? true);
  const tryParsePrompt = useTryParsePrompt(simpleMode);
  const authority = useAuthority();

  if (!pageData.id) {
    setReferenceImagePageData({
      ...pageData,
      id: uuid(),
    });
  }

  const defaultValues = useMemo(() => {
    return {
      product_name: '',
      extra_prompt: void 0,
      multi_prompt: void 0,
      aspect_ratio: '9:16',
      model: DEFAULT_REFERENCE_IMAGE_MODEL,
      file_list: [],
      prompt: '',
      duration: props.isLongVideoSupported ? 30 : 15,
      avatar_ids: [],
      generate_audio: true,
    } satisfies DefaultValues<FormValues>;
  }, []);

  const form = useForm({
    defaultValues: defaultValues,
    disabled: translate.isPending,
    resolver: yupResolver(schema),
    context: {
      simple_mode: simpleMode,
      generation_type: 'reference',
      is_long_video_supported: props.isLongVideoSupported,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    subscribe,
    setValue,
    getValues,
    formState: { isValid, isDirty },
    trigger,
    getFieldState,
  } = form;
  const lens = useLens({ control });

  const generate = useMutation(getGenerateVideoTaskMutationOptions());

  useEffect(() => {
    return subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const { file_list, prompt, multi_prompt, extra_prompt, ...rest } =
          values;

        setReferenceImagePageData((prevPageData) => {
          return {
            ...prevPageData,
            ...rest,
            file_list: toFileInputDtoList(file_list),
            prompt:
              (prevPageData.simple_mode ?? true)
                ? (prompt ?? '')
                : buildMultiPrompt(multi_prompt, extra_prompt),
            avatar_ids: values.avatar_ids ?? [],
          };
        });
      },
    });
  }, [setReferenceImagePageData, subscribe]);

  const toFormValues = useCallback(
    (
      data: ScriptDto | undefined,
      model: VideoModelCode,
    ): Partial<FormValues> => {
      return {
        file_list: toFileListFormValues(data?.file_list ?? []),
        duration: data?.duration,
        aspect_ratio: data?.aspect_ratio,
        product_name: data?.product_name,
        model: model,
        ...tryParsePrompt(data?.prompt ?? ''),
        avatar_ids: data?.avatar_ids,
        generate_audio: data?.generate_audio ?? true,
      };
    },
    [tryParsePrompt],
  );

  useEffect(() => {
    reset(toFormValues(pageData, DEFAULT_REFERENCE_IMAGE_MODEL));
  }, []);

  const handleGenerateVideo = handleSubmit(
    async (values) => {
      lfq('track', 'video.generate');

      const requestBody: GenerateVideoInputDto = {
        id: pageData.id,
        template_id: pageData.template_id,
        long_video: props.isLongVideoSupported,
        duration: props.isLongVideoSupported ? 0 : values.duration,
        prompt: simpleMode
          ? (values.prompt ?? '')
          : buildMultiPrompt(values.multi_prompt, values.extra_prompt),
        file_list: toFileInputDtoList(values.file_list),
        generation_type: GenerationType.Reference,
        aspect_ratio: values.aspect_ratio || '9:16',
        model: values.model,
        product_name: values.product_name,
        // TODO: 这些字段都不是用户填写的，应该由后端自己从数据库获取
        resolution: '720p',
        size: values.aspect_ratio === '16:9' ? '1280x720' : '720x1280',
        filename: pageData.filename,
        product_description: pageData.product_description,
        product_image_filename: pageData.product_image_filename,
        target_region: pageData.target_region,
        avatar_ids: values.avatar_ids,
        simple_mode: simpleMode,
        source: pageData.source,
        generate_audio: values.generate_audio,
      };

      if (!simpleMode) {
        const spec = videoModelSpecs.get(requestBody.model);

        if (spec?.max_prompt && requestBody.prompt.length > spec.max_prompt) {
          notification.error({
            title: '表单校验失败',
            description: `自定义分镜总提示词长度超过 ${spec.max_prompt} 字符，请修改后重试`,
          });
          return;
        }
      }

      lfq('track', 'video.generate.reference_image');
      const data = await generate.mutateAsync(requestBody);

      // 触发一次保存
      message.success(`视频生成任务已提交！Task ID: ${data.id}`);
    },
    (error) => {
      const promptError = error.prompt?.message || error.prompt?.root?.message;

      if (promptError) {
        notification.error({
          title: '表单校验失败',
          description: promptError,
        });
      }

      console.error(error);
    },
  );

  const handleSaveScript = async (data: ScriptDto) => {
    lfq('track', 'script.save');

    await trigger();
    if (!isValid) {
      notification.error({
        title: '表单校验失败',
        description: '请检查表单数据是否符合要求',
      });
      return;
    }

    await saveScript({
      type: ScriptType.Video,
      tags: null,
      data: {
        ...data,
        generation_type: GenerationType.Reference,
      },
    });

    message.success('脚本保存成功！');
  };

  const handleTranslate = async (lang: string) => {
    trigger();

    const ok = await new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (getFieldState('prompt').invalid) {
          notification.error({
            title: '表单校验失败',
            description: '请修正脚本配置的错误后重试',
          });
          return resolve(false);
        }

        return resolve(true);
      }, 0);
    });

    if (!ok) {
      return;
    }

    const loadingKey = uuid();

    try {
      message.open({
        key: loadingKey,
        type: 'loading',
        content: '正在为你设置语种，请稍候',
      });
      const result = await translate.mutateAsync({
        prompt: simpleMode
          ? (getValues('prompt') ?? '')
          : buildMultiPrompt(
              getValues('multi_prompt'),
              getValues('extra_prompt'),
            ),
        target_language: lang,
      });

      message.success(`“旁白/对话/字幕”语种已更改为“${lang}”`);

      if (simpleMode) {
        setValue('prompt', result.prompt);
      } else {
        try {
          const { extra_prompt, multi_prompt: segments } = parsePrompt(
            result.prompt,
          );
          setValue('extra_prompt', extra_prompt);
          setValue('multi_prompt', segments);
        } catch (error) {
          if (error instanceof ParseError) {
            setSimpleMode(true);
            setValue('prompt', result.prompt);
            return;
          }
          throw error;
        }
      }
    } finally {
      message.destroy(loadingKey);
    }
  };

  const onCheckPrompt = async () => {
    const prompt = simpleMode
      ? (getValues('prompt') ?? '')
      : buildMultiPrompt(getValues('multi_prompt'), getValues('extra_prompt'));

    if (!prompt.trim().length) {
      message.info('请输入提示词');
      return;
    }

    const overlayRef = openDrawer(
      <ComplianceDrawer prompt={prompt}></ComplianceDrawer>,
    );
    const newPrompt = await overlayRef.afterClosed;

    if (newPrompt) {
      try {
        if (simpleMode) {
          setValue('prompt', newPrompt);
        } else {
          const { extra_prompt, multi_prompt: segments } =
            parsePrompt(newPrompt);
          setValue('multi_prompt', segments);
          setValue('extra_prompt', extra_prompt);
        }
      } catch (error) {
        if (error instanceof ParseError) {
          setSimpleMode(true);
          setValue('prompt', newPrompt);
          return;
        }

        throw error;
      }
    }
  };

  const accept = useAccept(['.jpg', '.jpeg', '.png', '.webp', '.mp4', '.mp3']);
  useAutoPaste('#uploader', {
    filter: accept.filter,
  });

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左右布局，左侧操作面板，占据60% */}
      <div className="card flex-none animate-fade-in sm:overflow-auto xl:w-[480px]">
        <Form layout="vertical">
          <FormControl
            control={control}
            name="product_name"
            label="产品名称"
            required
            render={({ field }) => (
              <Input {...field} placeholder="请输入产品名称"></Input>
            )}
            extra={<ProductNameTip></ProductNameTip>}
          ></FormControl>
        </Form>
        <Button
          classNames={{ root: 'mb-2' }}
          variant="filled"
          color="default"
          block
          onClick={async () => {
            const overlayRef = openOverlay(
              <UploadedImageListModal></UploadedImageListModal>,
            );

            const file = await overlayRef.afterClosed;

            if (!file) {
              return;
            }

            const url = tryURL(file.oss_object_key, file.oss_host);

            if (!url) {
              return;
            }

            setValue('file_list', [
              ...getValues('file_list'),
              {
                uid: uuid(),
                name: file.filename,
                url: url.toString(),
                thumbUrl: url.toString(),
                status: 'done',
                response: {
                  id: file.id,
                  filename: file.filename,
                  mime: file.mime,
                  file_url: url.toString(),
                  created_at: file.created_at,
                },
              },
            ]);
          }}
        >
          从图片库选择
        </Button>
        <FormControl
          control={control}
          name="file_list"
          exact
          render={({ field }) => (
            <Upload.Dragger
              id="uploader"
              ref={field.ref}
              name={field.name}
              disabled={field.disabled}
              classNames={{ list: 'mt-2' }}
              listType="picture-card"
              multiple
              fileList={field.value}
              accept={accept}
              beforeUpload={createBeforeUpload({
                validator: FileValidators.compose([
                  createUploadValidator({ accept: accept.format }),
                  FileValidators.whenMime('video/*', referenceVideoValidator),
                ]),
                onError: ({ title, message }) => {
                  notification.error({ title, description: message });
                },
              })}
              onPreview={onPreview}
              iconRender={(file) => {
                if (file.type?.startsWith('audio/')) {
                  return (
                    <div className="flex h-full w-full items-center justify-center">
                      <AudioOutlined />
                    </div>
                  );
                }

                if (file.type?.startsWith('video/')) {
                  let thumbnail = '';

                  if (file.response?.file_url) {
                    const url = tryURL(file.response.file_url);

                    if (url) {
                      url.searchParams.set(
                        'x-oss-process',
                        'video/snapshot,t_1000,f_jpg,w_320,h_320',
                      );
                      thumbnail = url.toString();
                    }
                  }

                  if (thumbnail) {
                    return (
                      <div
                        className="flex h-full w-full items-center justify-center"
                        style={{
                          backgroundImage: `url("${thumbnail}")`,
                          backgroundSize: 'cover',
                        }}
                      >
                        <VideoCameraOutlined className="opacity-9 !text-white !text-opacity-80" />
                      </div>
                    );
                  }

                  return (
                    <div className="flex h-full w-full items-center justify-center">
                      <VideoCameraTwoTone />
                    </div>
                  );
                }

                return (
                  <img
                    src={file.thumbUrl || file.url}
                    alt={file.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                );
              }}
              showUploadList={{
                showRemoveIcon: true,
              }}
              onChange={(info) => {
                const { status } = info.file;

                if (status === 'error') {
                  notification.error({
                    title: '错误',
                    description: `图片 ${info.file.name} 上传失败，请重试`,
                  });
                }

                field.onChange(
                  info.fileList.map((item) => {
                    if (item.response) {
                      item.url = item.response.file_url;
                      item.thumbUrl = item.response.file_url;
                    }

                    return item;
                  }),
                );
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                点击、拖放或<span className="text-blue-500">粘贴</span>
                上传产品参考图片/视频
              </p>
              <p className="ant-upload-hint">支持图片与视频同时批量上传</p>
            </Upload.Dragger>
          )}
        ></FormControl>
        <Watch
          control={control}
          name="model"
          compute={(value) => {
            return isSupportedAssets(value);
          }}
          render={(visible) =>
            visible && (
              <FormControl
                layout="vertical"
                control={control}
                name="avatar_ids"
                label="虚拟人像"
                render={({ field }) => (
                  <VirtualAvatarSelect
                    mode="multiple"
                    {...field}
                  ></VirtualAvatarSelect>
                )}
              ></FormControl>
            )
          }
        ></Watch>
        <Form
          layout="vertical"
          classNames={{
            root: 'grid grid-cols-1 sm:grid-cols-2 gap-x-4',
          }}
        >
          {!props.isLongVideoSupported && (
            <FormControl
              control={control}
              name="model"
              label="生成平台"
              render={({ field }) => {
                return (
                  <Select
                    {...field}
                    allowClear={false}
                    popupMatchSelectWidth={false}
                    options={REFERENCE_IMAGE_MODEL_MAP.entries()
                      .filter(([_, spec]) => authority(spec.permission))
                      .filter(([_, spec]) =>
                        props.isLongVideoSupported ? !!spec.long_video : true,
                      )
                      .map(([code, spec]) => ({
                        label: `${spec.name}(${spec.credit})`,
                        value: code,
                      }))
                      .toArray()}
                    onChange={(value) => {
                      field.onChange(value);
                      trigger('duration');
                      trigger('prompt');
                    }}
                  ></Select>
                );
              }}
            ></FormControl>
          )}
          <FormControl
            control={control}
            name="aspect_ratio"
            label="宽高比"
            render={({ field }) => (
              <AspectRatioSelect {...field}></AspectRatioSelect>
            )}
          ></FormControl>
          {!props.isLongVideoSupported && (
            <FormControl
              control={control}
              name="duration"
              label="时长"
              tooltip={{
                classNames: { root: 'min-w-fit' },
                title: (
                  <DurationTooltip
                    generationType={GenerationType.Reference}
                    isLongVideoSupported={props.isLongVideoSupported}
                  ></DurationTooltip>
                ),
              }}
              render={({ field }) => (
                <InputNumber
                  classNames={{ root: 'w-full' }}
                  {...field}
                  min={0}
                  precision={0}
                  suffix="秒"
                ></InputNumber>
              )}
            ></FormControl>
          )}
          <FormControl
            control={control}
            name="generate_audio"
            label="输出声音"
            render={({ field }) => {
              return (
                <Segmented
                  options={[
                    { value: true, label: '有声' },
                    { value: false, label: '无声' },
                  ]}
                  {...field}
                ></Segmented>
              );
            }}
          ></FormControl>
        </Form>
        <div className="sticky bottom-0 flex space-x-2">
          <Popconfirm
            title="确定要重置吗？"
            onConfirm={() => {
              reset(defaultValues);
              setReferenceImagePageData(RESET);
            }}
          >
            <Button size="large" variant="solid" color="primary">
              重置
            </Button>
          </Popconfirm>
          <Popconfirm
            title="确定要生成视频吗？"
            onConfirm={handleGenerateVideo}
          >
            <Button
              variant="solid"
              color="primary"
              size="large"
              disabled={translate.isPending}
              loading={generate.isPending}
              block
            >
              {generate.isPending ? '生成中...' : '✨ 生成视频'}
            </Button>
          </Popconfirm>
          <Button
            variant="solid"
            color="primary"
            size="large"
            onClick={onCheckPrompt}
          >
            敏感内容检测
          </Button>
        </div>
        <VideoGenerationTaskTable
          taskType={[TaskType.视频生成, TaskType.长视频生成]}
          scriptId={pageData.id}
          onRowClick={async (task) => {
            const data = (task as VideoGenerationTaskDto).generate_data;

            if (isDirty) {
              const confirm = await modal.confirm({
                title: '查看脚本',
                content: '确定展示该历史脚本？该操作会覆盖当前脚本',
              });

              if (!confirm) {
                return;
              }
            }
            reset(toFormValues(data, getValues('model')));
          }}
        ></VideoGenerationTaskTable>
      </div>

      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:overflow-auto xl:flex-1">
        <div className="space-y-3">
          {props.isLongVideoSupported && (
            <Alert
              type="info"
              showIcon
              title={
                <>
                  长视频推荐使用
                  <Tooltip
                    placement="bottom"
                    classNames={{ root: 'min-w-fit' }}
                    title={
                      <>
                        <h4 className="my-2 font-bold">推荐脚本格式</h4>
                        <div className="rounded-md border bg-gray-50 p-2">
                          ## 镜头脚本
                          <br />
                          <br />
                          [镜号]：1，<span className="font-bold">必填</span>
                          <br />
                          [时长]：4秒，<span className="font-bold">必填</span>
                          <br />
                          [镜头目的]：输入镜头目的，可省略
                          <br />
                          [镜头]：输入镜头，可省略
                          <br />
                          [运镜]：输入运镜，可省略
                          <br />
                          [画面描述]：输入画面描述，
                          <span className="font-bold">必填</span>
                          <br />
                          [台词/字幕/情绪]：输入台词/字幕/情绪，可省略
                          <br />
                          [画面关键词]：输入画面关键词，可省略
                          <br />
                          ...
                          <br />
                          [镜号]：...
                          <br />
                          [时长]：...
                          <br />
                          [画面描述]：...
                          <br />
                          <br />
                          ## 要点
                          <br />
                          <br />
                          输入视频内容要点，可省略
                        </div>
                      </>
                    }
                  >
                    <span className="font-bold">
                      我们的视频脚本格式
                      <QuestionCircleOutlined className="mx-1" />
                    </span>
                  </Tooltip>
                  或自定义分镜 ，AI识别更完善、更快速
                </>
              }
            ></Alert>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="solid"
              color="primary"
              onClick={() => {
                handleSaveScript(pageData);
              }}
            >
              保存脚本
            </Button>
            <Segmented<number>
              value={+!!simpleMode}
              options={[
                { label: '简易模式', value: 1 },
                { label: '自定义分镜', value: 0 },
              ]}
              onChange={async (value) => {
                try {
                  if (value) {
                    setValue(
                      'prompt',
                      buildMultiPrompt(
                        getValues('multi_prompt'),
                        getValues('extra_prompt'),
                      ),
                    );
                  } else {
                    const { extra_prompt, multi_prompt: segments } =
                      parsePrompt(getValues('prompt'));
                    setValue('multi_prompt', segments);
                    setValue('extra_prompt', extra_prompt);
                  }
                } catch (error) {
                  if (error instanceof ParseError) {
                    notification.error({
                      title: '无法切换自定义分镜',
                      description: `提示词解析失败${error.message}`,
                    });
                    return;
                  }

                  throw error;
                }

                setSimpleMode(!!value);
                setReferenceImagePageData((prevPageData) => {
                  return {
                    ...prevPageData,
                    simple_mode: !!value,
                  };
                });
              }}
            />
            {simpleMode && (
              <Button
                variant="filled"
                color="default"
                onClick={() => {
                  const value = getValues('prompt');
                  if (value?.length) {
                    modal.confirm({
                      title: '初始化脚本',
                      content: '初始化脚本会清空现有脚本内容，确定要初始化吗？',
                      onOk: () => {
                        setValue('prompt', scriptTemplate);
                      },
                    });

                    return;
                  }

                  setValue('prompt', scriptTemplate);
                }}
              >
                初始化脚本
              </Button>
            )}
            <div className="flex-auto"></div>
            <LanguageDropdownControl
              isLoading={translate.isPending}
              onTranslate={handleTranslate}
            ></LanguageDropdownControl>
          </div>
          <DurationAlert></DurationAlert>
          <Watch
            control={control}
            name={['file_list', 'avatar_ids', 'model']}
            render={([file_list, avatar_ids, model]) => {
              return simpleMode ? (
                <PromptControl
                  generationType={GenerationType.Reference}
                  images={file_list}
                  asset_ids={
                    isSupportedAssets(model) ? (avatar_ids ?? void 0) : void 0
                  }
                  modelLens={lens.focus('model')}
                  lens={lens.focus('prompt').cast()}
                ></PromptControl>
              ) : (
                <MultiPromptControl
                  generationType={GenerationType.Reference}
                  images={file_list}
                  modelLens={lens.focus('model')}
                  lens={lens.focus('multi_prompt').cast()}
                ></MultiPromptControl>
              );
            }}
          ></Watch>
          {!simpleMode && (
            <FormControl
              layout="vertical"
              control={control}
              name="extra_prompt"
              label="额外说明"
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  value={field.value ?? ''}
                  rows={4}
                ></Input.TextArea>
              )}
            ></FormControl>
          )}
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_creator-center/video/reference-create')(
  {
    staticData: { permissions: [Permission.视频生成] },
    component: VideoCreateMultiImage,
  },
);
